/**
 * /gt/:code — one native gathering seat (Oct 2026). The QR IS this address, so the gathering
 * door's scanner (which pulls the UUID out of whatever it reads) resolves the seat code and
 * nothing else — an event door never admits it. Holder, buyer or host only; everyone else gets
 * NOT FOUND, never the seat.
 *
 * On the black ticket vocabulary My Tickets uses. The holder can pass the seat on (one claim
 * link, retires this QR when claimed) or take an open offer back.
 */
import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams, Link } from 'react-router-dom';
import gatheringService from '../services/gatheringService';
import { LoadingSpinner } from '../components';
import { webOrigin } from '../utils/brand';
import { useDialog } from '../context/DialogContext';
import { dayRangeLabel } from '../utils/surfaceFormat';
import { API_BASE } from '../utils/apiBase';

const STATUS_COPY = {
  paid: ['VALID FOR ENTRY', 'text-green-400'], refund_pending: ['REFUND IN PROGRESS · NOT VALID', 'text-red-400'],
  refund_failed: ['REFUND NEEDS ATTENTION · NOT VALID', 'text-red-400'], refunded: ['REFUNDED · NOT VALID', 'text-gray-500'],
  accepted: ['NOT PAID YET', 'text-yellow-400'], pending: ['WAITING ON THE HOST', 'text-yellow-400'],
};

export function seatQrUrl(code) {
  const url = `${webOrigin()}/gt/${code}`;
  return { url, qr: `https://api.qrserver.com/v1/create-qr-code/?data=${encodeURIComponent(url)}&size=240x240&bgcolor=000000&color=ffffff` };
}

export default function GatheringTicketPage() {
  const { code } = useParams();
  const navigate = useNavigate();
  const { showAlert } = useDialog();
  const [ticket, setTicket] = useState(null);
  const [loading, setLoading] = useState(true);
  const [offering, setOffering] = useState(false);
  const [email, setEmail] = useState('');
  const [busy, setBusy] = useState(false);
  const [copied, setCopied] = useState(false);
  // Which phone wallets this environment can issue. Same capability probe the event ticket uses.
  const [wallet, setWallet] = useState({ applePass: false, googlePass: false });
  useEffect(() => {
    fetch(`${API_BASE}/tickets/wallet/capabilities`)
      .then((r) => r.json())
      .then((d) => setWallet({ applePass: !!d?.data?.applePass, googlePass: !!d?.data?.googlePass }))
      .catch(() => {});
  }, []);

  const load = useCallback(async () => {
    const r = await gatheringService.getTicketByCode(code);
    setTicket(r.success ? r.data : null);
    setLoading(false);
  }, [code]);
  useEffect(() => { load(); }, [load]);

  const offer = async () => {
    setBusy(true);
    const r = await gatheringService.createTransfer(ticket.id, { email: email.trim() || null });
    setBusy(false);
    if (!r.success) { await showAlert({ title: 'COULD NOT MAKE THE OFFER', message: r.error?.message || r.error || 'Something went wrong.', type: 'error' }); return; }
    setOffering(false);
    setEmail('');
    load();
  };
  const cancelOffer = async () => {
    setBusy(true);
    await gatheringService.cancelTransfer(ticket.pendingTransfer.id);
    setBusy(false);
    load();
  };
  const copy = async (text) => {
    try { await navigator.clipboard.writeText(text); setCopied(true); window.setTimeout(() => setCopied(false), 1800); } catch { /* no clipboard */ }
  };

  if (loading) return <div className="flex min-h-screen items-center justify-center bg-black"><LoadingSpinner /></div>;
  if (!ticket) {
    return (
      <div className="min-h-screen bg-black px-6 py-16 text-white">
        <div className="mx-auto max-w-md border-2 border-white p-8 text-center" data-testid="seat-missing">
          <p className="font-mono text-lg font-bold uppercase tracking-wider">SEAT NOT FOUND</p>
          <p className="mx-auto mt-3 max-w-xs font-mono text-xs leading-relaxed text-gray-500">This code isn't a seat you hold. If it was passed on, the old code stopped working when it was claimed.</p>
          <Link to="/my-tickets" className="mt-6 inline-block border border-white px-6 py-3 font-mono text-xs font-bold uppercase tracking-widest hover:bg-white hover:text-black">MY TICKETS</Link>
        </div>
      </div>
    );
  }

  const t = ticket;
  const { url, qr } = seatQrUrl(t.ticketCode);
  const [statusLabel, statusClass] = STATUS_COPY[t.status] || [String(t.status || '').toUpperCase().replace(/_/g, ' '), 'text-gray-400'];
  const valid = t.status === 'paid' && !t.isFrozen;
  const gatheringPath = t.clubId ? `/clubs/${t.clubId}/gatherings/${t.gatheringId}` : `/gatherings/${t.gatheringId}`;
  const holder = t.viewerIsBuyer ? !t.isTransferred : true;

  return (
    <div className="min-h-screen bg-black text-white">
      <div className="mx-auto max-w-md px-5 py-6" data-testid="gathering-seat">
        <div className="mb-4 flex items-center justify-between">
          <button type="button" onClick={() => navigate('/my-tickets')} className="font-mono text-[10px] uppercase tracking-widest text-gray-500 hover:text-white">← MY TICKETS</button>
          <span className="border border-white/40 px-2.5 py-1 font-mono text-[10px] font-bold uppercase tracking-widest">GATHERING SEAT</span>
        </div>
        <div className="border border-white/20">
          {t.coverUrl && <img src={t.coverUrl} alt="" referrerPolicy="no-referrer" className="h-40 w-full object-cover" />}
          <div className="border-b border-white/10 p-5">
            <h1 className="font-mono text-2xl font-bold leading-tight" data-testid="seat-gathering">{t.gatheringName}</h1>
            <p className="mt-1 font-mono text-xs uppercase tracking-wider text-gray-400">{dayRangeLabel(t.startsAt, t.endsAt)}</p>
            <Link to={gatheringPath} className="mt-2 inline-block font-mono text-[10px] uppercase tracking-widest text-gray-500 hover:text-white">See the gathering →</Link>
          </div>
          <div className="flex items-center justify-between border-b border-white/10 px-5 py-3">
            <p className="font-mono text-[10px] tracking-wider text-gray-500">SEAT {t.ticketNumber} OF {t.totalInBatch}{t.isTransferred && !t.viewerIsBuyer && <span className="ml-2 text-dreamPink">TRANSFERRED TO YOU</span>}{t.isTransferred && t.viewerIsBuyer && <span className="ml-2 text-gray-500">PASSED ON</span>}</p>
            <p className={`font-mono text-[10px] font-bold ${t.checkedInAt ? 'text-green-400' : statusClass}`} data-testid="seat-status">{t.checkedInAt ? 'CHECKED IN' : statusLabel}{t.isFrozen && !t.checkedInAt ? ' · FROZEN' : ''}</p>
          </div>
          <div className="flex flex-col items-center p-5">
            <p className="mb-3 font-mono text-[10px] tracking-wider text-gray-500">{valid ? 'SCAN AT THE GATHERING DOOR' : 'THIS SEAT DOES NOT ADMIT RIGHT NOW'}</p>
            {valid && !(t.isTransferred && t.viewerIsBuyer) ? <img src={qr} alt="Seat QR" className="h-[200px] w-[200px]" data-testid="seat-qr" /> : <div className="flex h-[200px] w-[200px] items-center justify-center border border-white/20 font-mono text-[10px] uppercase tracking-widest text-gray-600">NO QR</div>}
            <p className="mt-3 font-mono text-[10px] text-gray-600">{t.ticketCode}</p>
            {valid && !(t.isTransferred && t.viewerIsBuyer) && (wallet.applePass || wallet.googlePass) && (
              <div className="mt-4 flex w-full flex-col gap-2" data-testid="seat-wallet">
                {wallet.applePass && (
                  <a href={`${API_BASE}/gatherings/tickets/${t.ticketCode}/apple-pass`}
                    className="w-full rounded-lg bg-white py-2.5 text-center font-mono text-xs font-bold tracking-wider text-black hover:opacity-90"> Add to Apple Wallet</a>
                )}
                {wallet.googlePass && (
                  <a href={`${API_BASE}/gatherings/tickets/${t.ticketCode}/google-pass`}
                    className="w-full rounded-lg border border-white bg-black py-2.5 text-center font-mono text-xs font-bold tracking-wider text-white hover:opacity-90">Add to Google Wallet</a>
                )}
              </div>
            )}
          </div>
          {holder && (
            <div className="border-t border-white/10 p-5" data-testid="seat-transfer">
              {t.pendingTransfer ? (
                <div className="space-y-3">
                  <p className="font-mono text-[10px] uppercase tracking-widest text-dreamPink">OFFER OPEN{t.pendingTransfer.sentToEmail ? ` · SENT TO ${t.pendingTransfer.sentToEmail}` : ''}</p>
                  <p className="break-all font-mono text-xs text-gray-400">{t.pendingTransfer.claimUrl}</p>
                  <div className="flex gap-2">
                    <button type="button" onClick={() => copy(t.pendingTransfer.claimUrl)} className="flex-1 border border-white py-2.5 font-mono text-[10px] font-bold uppercase tracking-widest hover:bg-white hover:text-black" data-testid="copy-offer">{copied ? 'COPIED' : 'COPY LINK'}</button>
                    <button type="button" onClick={cancelOffer} disabled={busy} className="flex-1 border border-red-500 py-2.5 font-mono text-[10px] font-bold uppercase tracking-widest text-red-400 hover:bg-red-500 hover:text-white" data-testid="cancel-offer">TAKE IT BACK</button>
                  </div>
                  <p className="font-mono text-[10px] leading-relaxed text-gray-600">Whoever opens the link first gets the seat. This QR stops working the moment they do.</p>
                </div>
              ) : offering ? (
                <div className="space-y-3">
                  <p className="font-mono text-[10px] uppercase tracking-widest text-gray-500">PASS THIS SEAT ON</p>
                  <input value={email} onChange={(e) => setEmail(e.target.value)} placeholder="Their email (optional — or just copy the link)" aria-label="Recipient email" className="w-full border border-white/40 bg-black px-3 py-2.5 font-mono text-xs text-white outline-none focus:border-white" />
                  <div className="flex gap-2">
                    <button type="button" onClick={offer} disabled={busy} className="flex-1 bg-white py-2.5 font-mono text-[10px] font-bold uppercase tracking-widest text-black hover:opacity-90 disabled:opacity-40" data-testid="make-offer">{busy ? 'MAKING…' : 'MAKE THE OFFER'}</button>
                    <button type="button" onClick={() => setOffering(false)} className="border border-white/40 px-4 py-2.5 font-mono text-[10px] font-bold uppercase tracking-widest text-gray-400 hover:text-white">CANCEL</button>
                  </div>
                </div>
              ) : t.transferBlockedReason ? (
                <p className="font-mono text-[10px] leading-relaxed text-gray-500" data-testid="transfer-blocked">{t.transferBlockedReason}</p>
              ) : (
                <button type="button" onClick={() => setOffering(true)} className="w-full border border-white py-3 font-mono text-[10px] font-bold uppercase tracking-widest hover:bg-white hover:text-black" data-testid="start-offer">PASS THIS SEAT ON</button>
              )}
            </div>
          )}
          <div className="border-t border-white/10 px-5 py-3">
            <button type="button" onClick={() => copy(url)} className="font-mono text-[10px] uppercase tracking-widest text-gray-500 hover:text-white">{copied ? 'COPIED' : 'COPY SEAT LINK'}</button>
          </div>
        </div>
      </div>
    </div>
  );
}
